// store/slices/surgeryScheduleSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import BaseUrl from '../../BaseUrl';

// Create Axios instance for surgery API
const surgeryAPI = axios.create({
  baseURL: 'https://laafisurgery.azurewebsites.net/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
});

const authAPI = axios.create({
  baseURL: BaseUrl.authurl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Async thunk for new patient registration
export const registerPatient = createAsyncThunk(
  'surgerySchedule/registerPatient',
  async (patientData, { rejectWithValue }) => {
    try {
      
      
      const response = await authAPI.post('Auth/Login', patientData, {
        headers: {
          'Content-Type': 'application/json',
          'accept': '*/*',
        },
      });
      
      
      return response.data;
    } catch (error) {
      console.error('Registration error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Registration failed');
    }
  }
);

// Async thunk for fetching patients
export const fetchPatients = createAsyncThunk(
  'surgerySchedule/fetchPatients',
  async (searchQuery, { rejectWithValue }) => {
    try {
      if (!searchQuery || searchQuery.length < 2) {
        return [];
      }

      
      const response = await authAPI.get(
        `${BaseUrl.authurl}Auth/GetPatients?id=${encodeURIComponent(searchQuery)}`
      );

      
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        
        return [];
      }
      console.error('Fetch patients error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch patients');
    }
  }
);

// Async thunk for fetching surgical procedures
export const fetchSurgicalProcedures = createAsyncThunk(
  'surgerySchedule/fetchSurgicalProcedures',
  async (_, { rejectWithValue }) => {
    try {
      const response = await surgeryAPI.get('/SurgicalProcedureMasters');
      
      return response.data;
    } catch (error) {
      console.error('Fetch procedures error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch procedures');
    }
  }
);

// Async thunk for fetching doctors
export const fetchDoctors = createAsyncThunk(
  'surgerySchedule/fetchDoctors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.get('Auth/GetAllDoctors');
      
      return response.data;
    } catch (error) {
      console.error('Fetch doctors error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch doctors');
    }
  }
);

// Async thunk for fetching rooms
export const fetchRooms = createAsyncThunk(
  'surgerySchedule/fetchRooms',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BaseUrl.hmsurl}Rooms`);
      
      return response.data;
    } catch (error) {
      console.error('Fetch rooms error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch rooms');
    }
  }
);

// Async thunk for submitting surgery allocation
export const submitSurgeryAllocation = createAsyncThunk(
  'surgerySchedule/submitSurgeryAllocation',
  async (allocationData, { rejectWithValue }) => {
    try {
      
      
      const response = await surgeryAPI.post('/SurgerySchedules', allocationData, {
        headers: {
          'Content-Type': 'application/json',
          'accept': '*/*',
        },
      });

      
      return response.data;
    } catch (error) {
      console.error('Allocation submission error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to submit allocation');
    }
  }
);

// Async thunk for fetching roles
export const fetchRoles = createAsyncThunk(
  'surgerySchedule/fetchRoles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.get('Auth/GetRoles');
      return response.data;
    } catch (error) {
      console.error('Fetch roles error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch roles');
    }
  }
);

// Async thunk for fetching staff by role
export const fetchStaffByRole = createAsyncThunk(
  'surgerySchedule/fetchStaffByRole',
  async ({ roleName, clientId }, { rejectWithValue }) => {
    try {
      const response = await authAPI.get(
        `Auth/GetUsersInRole?role=${encodeURIComponent(roleName)}&clientId=${clientId}`
      );
      return response.data;
    } catch (error) {
      console.error('Fetch staff error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch staff');
    }
  }
);

// Async thunk for submitting staff assignments
export const submitStaffAssignments = createAsyncThunk(
  'surgerySchedule/submitStaffAssignments',
  async (assignments, { rejectWithValue }) => {
    try {
      const responses = await Promise.all(
        assignments.map(assignment =>
          surgeryAPI.post('/SurgeryStaffAssignments', assignment, {
            headers: {
              'Content-Type': 'application/json',
            },
          })
        )
      );
      return responses.map(response => response.data);
    } catch (error) {
      console.error('Staff assignment error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to assign staff');
    }
  }
);

// Async thunk for fetching equipment categories
export const fetchEquipmentCategories = createAsyncThunk(
  'surgerySchedule/fetchEquipmentCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await surgeryAPI.get('/EquipmentCategories');
      return response.data;
    } catch (error) {
      console.error('Fetch equipment categories error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch equipment categories');
    }
  }
);

// Async thunk for fetching equipment type masters
export const fetchEquipmentTypeMasters = createAsyncThunk(
  'surgerySchedule/fetchEquipmentTypeMasters',
  async (_, { rejectWithValue }) => {
    try {
      const response = await surgeryAPI.get('/EquipmentTypeMasters');
      return response.data;
    } catch (error) {
      console.error('Fetch equipment types error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch equipment types');
    }
  }
);

// Async thunk for fetching equipment inventories
export const fetchEquipmentInventories = createAsyncThunk(
  'surgerySchedule/fetchEquipmentInventories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await surgeryAPI.get('/EquipmentInventories');
      return response.data;
    } catch (error) {
      console.error('Fetch equipment inventories error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch equipment inventories');
    }
  }
);

// Async thunk for fetching existing kit allocations
export const fetchKitAllocations = createAsyncThunk(
  'surgerySchedule/fetchKitAllocations',
  async (surgeryId, { rejectWithValue }) => {
    try {
      const response = await surgeryAPI.get(`/KitAllocations${surgeryId ? `?SurgeryID=${surgeryId}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Fetch kit allocations error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch kit allocations');
    }
  }
);

// Async thunk for creating kit allocation
export const createKitAllocation = createAsyncThunk(
  'surgerySchedule/createKitAllocation',
  async (allocationData, { rejectWithValue }) => {
    try {
      const response = await surgeryAPI.post('/KitAllocations', allocationData);
      return response.data;
    } catch (error) {
      console.error('Create kit allocation error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create kit allocation');
    }
  }
);

// Async thunk for deleting kit allocation
export const deleteKitAllocation = createAsyncThunk(
  'surgerySchedule/deleteKitAllocation',
  async (allocationId, { rejectWithValue }) => {
    try {
      await surgeryAPI.delete(`/KitAllocations/${allocationId}`);
      return allocationId;
    } catch (error) {
      console.error('Delete kit allocation error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete kit allocation');
    }
  }
);

const surgeryScheduleSlice = createSlice({
  name: 'surgerySchedule',
  initialState: {
    // Original state
    patients: [],
    surgicalProcedures: [],
    doctors: [],
    rooms: [],
    selectedPatient: null,
    searchQuery: '',
    loading: false,
    proceduresLoading: false,
    doctorsLoading: false,
    roomsLoading: false,
    submissionLoading: false,
    registrationLoading: false,
    registrationError: null,
    error: null,
    proceduresError: null,
    doctorsError: null,
    roomsError: null,
    submissionError: null,
    
    // Staff assignment state
    roles: [],
    staffMembers: [],
    selectedStaff: [],
    rolesLoading: false,
    staffLoading: false,
    assignmentLoading: false,
    assignmentError: null,
    
    // Kit allocation state
    equipmentCategories: [],
    equipmentTypeMasters: [],
    equipmentInventories: [],
    kitAllocations: [],
    selectedSurgeryForKit: null,
    categoriesLoading: false,
    typesLoading: false,
    inventoriesLoading: false,
    kitAllocationsLoading: false,
    kitAllocationSubmissionLoading: false,
    kitAllocationError: null,
    
    // Form states
    allocationForm: {
      SurgicalProcedure: 0,
      spm: {
        ProcedureID: 0,
        ProcedureName: '',
        Description: '',
        AverageDurationOfProcedure: ''
      },
      MRN: '',
      DoctorID: '',
      RoomID: 0,
      ScheduledDate: '',
      Duration: 0,
      AnesthesiaDosage: 0,
      Status: 'NotStarted',
      Notes: '',
    },
    kitAllocationForm: {
      EquipmentCategory: '',
      EquipmentTypeID: '',
      EquipmentInventoryID: '',
      qty: 1,
      SurgeryID: null
    }
  },
  reducers: {
    setSelectedPatient: (state, action) => {
      state.selectedPatient = action.payload;
      if (action.payload) {
        state.allocationForm.MRN = action.payload.id;
      }
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearPatients: (state) => {
      state.patients = [];
    },
    clearError: (state) => {
      state.error = null;
      state.proceduresError = null;
      state.doctorsError = null;
      state.roomsError = null;
      state.submissionError = null;
      state.registrationError = null;
      state.assignmentError = null;
      state.kitAllocationError = null;
    },
    clearAllocation: (state) => {
      state.selectedPatient = null;
      state.searchQuery = '';
      state.patients = [];
      state.allocationForm = {
        SurgicalProcedure: 0,
        spm: {
          ProcedureID: 0,
          ProcedureName: '',
          Description: '',
          AverageDurationOfProcedure: ''
        },
        MRN: '',
        DoctorID: '',
        RoomID: 0,
        ScheduledDate: '',
        Duration: 0,
        AnesthesiaDosage: 0,
        Status: 'NotStarted',
        Notes: ''
      };
    },
    updateAllocationForm: (state, action) => {
      state.allocationForm = {
        ...state.allocationForm,
        ...action.payload
      };
    },
    updateProcedureDetails: (state, action) => {
      state.allocationForm.spm = {
        ...state.allocationForm.spm,
        ...action.payload
      };
    },
    clearRegistrationError: (state) => {
      state.registrationError = null;
    },
    
    // Staff assignment reducers
    setSelectedRole: (state, action) => {
      state.selectedRole = action.payload;
    },
    setSelectedStaff: (state, action) => {
      state.selectedStaff = action.payload;
    },
    clearStaffSelection: (state) => {
      state.selectedStaff = [];
      state.staffMembers = [];
    },
    
    // Kit allocation reducers
    setSelectedSurgeryForKit: (state, action) => {
      state.selectedSurgeryForKit = action.payload;
      if (action.payload) {
        state.kitAllocationForm.SurgeryID = action.payload.SurgeryID;
      }
    },
    updateKitAllocationForm: (state, action) => {
      state.kitAllocationForm = {
        ...state.kitAllocationForm,
        ...action.payload
      };
    },
    clearKitAllocationForm: (state) => {
      state.kitAllocationForm = {
        EquipmentCategory: '',
        EquipmentTypeID: '',
        EquipmentInventoryID: '',
        qty: 1,
        SurgeryID: state.selectedSurgeryForKit?.SurgeryID || null
      };
    },
    clearKitAllocationData: (state) => {
      state.equipmentCategories = [];
      state.equipmentTypeMasters = [];
      state.equipmentInventories = [];
      state.kitAllocations = [];
      state.selectedSurgeryForKit = null;
      state.kitAllocationForm = {
        EquipmentCategory: '',
        EquipmentTypeID: '',
        EquipmentInventoryID: '',
        qty: 1,
        SurgeryID: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Patients
      .addCase(fetchPatients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.loading = false;
        state.patients = action.payload || [];
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.patients = [];
      })
      
      // Surgical Procedures
      .addCase(fetchSurgicalProcedures.pending, (state) => {
        state.proceduresLoading = true;
        state.proceduresError = null;
      })
      .addCase(fetchSurgicalProcedures.fulfilled, (state, action) => {
        state.proceduresLoading = false;
        state.surgicalProcedures = action.payload || [];
      })
      .addCase(fetchSurgicalProcedures.rejected, (state, action) => {
        state.proceduresLoading = false;
        state.proceduresError = action.payload;
        state.surgicalProcedures = [];
      })
      
      // Doctors
      .addCase(fetchDoctors.pending, (state) => {
        state.doctorsLoading = true;
        state.doctorsError = null;
      })
      .addCase(fetchDoctors.fulfilled, (state, action) => {
        state.doctorsLoading = false;
        state.doctors = action.payload || [];
      })
      .addCase(fetchDoctors.rejected, (state, action) => {
        state.doctorsLoading = false;
        state.doctorsError = action.payload;
        state.doctors = [];
      })
      
      // Rooms
      .addCase(fetchRooms.pending, (state) => {
        state.roomsLoading = true;
        state.roomsError = null;
      })
      .addCase(fetchRooms.fulfilled, (state, action) => {
        state.roomsLoading = false;
        state.rooms = action.payload || [];
      })
      .addCase(fetchRooms.rejected, (state, action) => {
        state.roomsLoading = false;
        state.roomsError = action.payload;
        state.rooms = [];
      })
      
      // Submit Allocation
      .addCase(submitSurgeryAllocation.pending, (state) => {
        state.submissionLoading = true;
        state.submissionError = null;
      })
      .addCase(submitSurgeryAllocation.fulfilled, (state) => {
        state.submissionLoading = false;
        // Clear form on successful submission
        state.selectedPatient = null;
        state.searchQuery = '';
        state.patients = [];
        state.allocationForm = {
          SurgicalProcedure: 0,
          spm: {
            ProcedureID: 0,
            ProcedureName: '',
            Description: '',
            AverageDurationOfProcedure: ''
          },
          MRN: '',
          DoctorID: '',
          RoomID: 0,
          ScheduledDate: '',
          Duration: 0,
          AnesthesiaDosage: 0,
          Status: 'NotStarted',
          Notes: ''
        };
      })
      .addCase(submitSurgeryAllocation.rejected, (state, action) => {
        state.submissionLoading = false;
        state.submissionError = action.payload;
      })
      
      // Patient Registration
      .addCase(registerPatient.pending, (state) => {
        state.registrationLoading = true;
        state.registrationError = null;
      })
      .addCase(registerPatient.fulfilled, (state, action) => {
        state.registrationLoading = false;
        state.registrationError = null;
        if (action.payload) {
          state.patients = [action.payload];
        }
      })
      .addCase(registerPatient.rejected, (state, action) => {
        state.registrationLoading = false;
        state.registrationError = action.payload;
      })
      
      // Roles
      .addCase(fetchRoles.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.roles = action.payload || [];
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      })
      
      // Staff by Role
      .addCase(fetchStaffByRole.pending, (state) => {
        state.staffLoading = true;
        state.staffError = null;
      })
      .addCase(fetchStaffByRole.fulfilled, (state, action) => {
        state.staffLoading = false;
        state.staffMembers = action.payload || [];
      })
      .addCase(fetchStaffByRole.rejected, (state, action) => {
        state.staffLoading = false;
        state.staffError = action.payload;
      })
      
      // Staff Assignments
      .addCase(submitStaffAssignments.pending, (state) => {
        state.assignmentLoading = true;
        state.assignmentError = null;
      })
      .addCase(submitStaffAssignments.fulfilled, (state) => {
        state.assignmentLoading = false;
        state.selectedStaff = [];
      })
      .addCase(submitStaffAssignments.rejected, (state, action) => {
        state.assignmentLoading = false;
        state.assignmentError = action.payload;
      })
      
      // Equipment Categories
      .addCase(fetchEquipmentCategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(fetchEquipmentCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.equipmentCategories = action.payload || [];
      })
      .addCase(fetchEquipmentCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload;
      })
      
      // Equipment Type Masters
      .addCase(fetchEquipmentTypeMasters.pending, (state) => {
        state.typesLoading = true;
        state.typesError = null;
      })
      .addCase(fetchEquipmentTypeMasters.fulfilled, (state, action) => {
        state.typesLoading = false;
        state.equipmentTypeMasters = action.payload || [];
      })
      .addCase(fetchEquipmentTypeMasters.rejected, (state, action) => {
        state.typesLoading = false;
        state.typesError = action.payload;
      })
      
      // Equipment Inventories
      .addCase(fetchEquipmentInventories.pending, (state) => {
        state.inventoriesLoading = true;
        state.inventoriesError = null;
      })
      .addCase(fetchEquipmentInventories.fulfilled, (state, action) => {
        state.inventoriesLoading = false;
        state.equipmentInventories = action.payload || [];
      })
      .addCase(fetchEquipmentInventories.rejected, (state, action) => {
        state.inventoriesLoading = false;
        state.inventoriesError = action.payload;
      })
      
      // Kit Allocations
      .addCase(fetchKitAllocations.pending, (state) => {
        state.kitAllocationsLoading = true;
        state.kitAllocationError = null;
      })
      .addCase(fetchKitAllocations.fulfilled, (state, action) => {
        state.kitAllocationsLoading = false;
        state.kitAllocations = action.payload || [];
      })
      .addCase(fetchKitAllocations.rejected, (state, action) => {
        state.kitAllocationsLoading = false;
        state.kitAllocationError = action.payload;
      })
      
      // Create Kit Allocation
      .addCase(createKitAllocation.pending, (state) => {
        state.kitAllocationSubmissionLoading = true;
        state.kitAllocationError = null;
      })
      .addCase(createKitAllocation.fulfilled, (state, action) => {
        state.kitAllocationSubmissionLoading = false;
        state.kitAllocations.push(action.payload);
        state.kitAllocationForm = {
          EquipmentCategory: '',
          EquipmentTypeID: '',
          EquipmentInventoryID: '',
          qty: 1,
          SurgeryID: state.selectedSurgeryForKit?.SurgeryID || null
        };
      })
      .addCase(createKitAllocation.rejected, (state, action) => {
        state.kitAllocationSubmissionLoading = false;
        state.kitAllocationError = action.payload;
      })
      
      // Delete Kit Allocation
      .addCase(deleteKitAllocation.fulfilled, (state, action) => {
        state.kitAllocations = state.kitAllocations.filter(
          allocation => allocation.Id !== action.payload
        );
      });
  },
});

export const {
  setSelectedPatient,
  setSearchQuery,
  clearPatients,
  clearError,
  clearAllocation,
  updateAllocationForm,
  updateProcedureDetails,
  clearRegistrationError,
  setSelectedRole,
  setSelectedStaff,
  clearStaffSelection,
  setSelectedSurgeryForKit,
  updateKitAllocationForm,
  clearKitAllocationForm,
  clearKitAllocationData
} = surgeryScheduleSlice.actions;

export default surgeryScheduleSlice.reducer;